<template>
  <div class="time-selector-container">
    <!-- 시간 선택 버튼 -->
    <div class="time-input-wrapper">
      <button class="time-display" @click="toggleTimePicker" :disabled="readonly">
        <div class="flex items-center justify-center">
          <NumberInput :showControls="false" :numberWidth="2" :value="selectedHour" @input="(value) => onTimeInput('h', value)" :min="0" :max="23" inputStyle="padding: 0;border: 0;text-align: center !important;" />
          <span>:</span>
          <NumberInput :showControls="false" :numberWidth="2" :value="selectedMinute" @input="(value) => onTimeInput('m', value)" :min="0" :max="59" inputStyle="padding: 0;border: 0;text-align: center !important;" />
          <span v-if="showSeconds">:</span>
          <NumberInput v-if="showSeconds" :showControls="false" :numberWidth="2" :value="selectedSecond" @input="(value) => onTimeInput('s', value)" :min="0" :max="59" inputStyle="padding: 0;border: 0;text-align: center !important;" />
        </div>
        <vs-icon icon-pack="feather" icon="icon-clock" class="clock-icon"></vs-icon>
      </button>
    </div>

    <!-- 시간 선택 패널 -->
    <div v-if="showTimePicker" class="time-picker-panel">
      <div class="time-picker-header">
        <span class="preview-time">{{ previewTime }}</span>
      </div>

      <div class="time-picker-content">
        <div class="time-column">
          <div class="column-label">시</div>
          <div class="time-scroller">
            <div v-for="hour in 24" :key="`hour-${hour - 1}`" class="time-item" :class="{ active: selectedHour === hour - 1, selected: tempHour === hour - 1 }" @click="selectHour(hour - 1)">
              {{ padZero(hour - 1) }}
            </div>
          </div>
        </div>

        <div class="time-divider">:</div>

        <div class="time-column">
          <div class="column-label">분</div>
          <div class="time-scroller">
            <div v-for="minute in 60" :key="`minute-${minute - 1}`" class="time-item" :class="{ active: selectedMinute === minute - 1, selected: tempMinute === minute - 1 }" @click="selectMinute(minute - 1)">
              {{ padZero(minute - 1) }}
            </div>
          </div>
        </div>

        <template v-if="showSeconds">
          <div class="time-divider">:</div>

          <div class="time-column">
            <div class="column-label">초</div>
            <div class="time-scroller">
              <div v-for="second in 60" :key="`second-${second - 1}`" class="time-item" :class="{ active: selectedSecond === second - 1, selected: tempSecond === second - 1 }" @click="selectSecond(second - 1)">
                {{ padZero(second - 1) }}
              </div>
            </div>
          </div>
        </template>
      </div>

      <div class="time-picker-footer">
        <button class="cancel-btn" @click="cancelSelection">취소</button>
        <button class="apply-btn" @click="applySelection">확인</button>
      </div>
    </div>
  </div>
</template>

<script>
import NumberInput from "@/components/NumberInput.vue";
export default {
  name: "TimeSelector",
  components: { NumberInput },
  props: {
    readonly: Boolean,
    value: {
      type: String,
      default: "",
    },
    showSeconds: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      time: this.value,
      showTimePicker: false,
      selectedHour: 0,
      selectedMinute: 0,
      selectedSecond: 0,
      tempHour: 0,
      tempMinute: 0,
      tempSecond: 0,
    };
  },
  computed: {
    formattedTime() {
      if (!this.time) return "";

      const timeParts = this.time.split(":");
      if (timeParts.length >= 2) {
        if (this.showSeconds && timeParts.length === 3) {
          return `${timeParts[0]}:${timeParts[1]}:${timeParts[2]}`;
        }
        return `${timeParts[0]}:${timeParts[1]}`;
      }
      return this.time;
    },
    previewTime() {
      if (this.showSeconds) {
        return `${this.padZero(this.tempHour)}:${this.padZero(this.tempMinute)}:${this.padZero(this.tempSecond)}`;
      }
      return `${this.padZero(this.tempHour)}:${this.padZero(this.tempMinute)}`;
    },
  },
  watch: {
    value(newValue) {
      this.time = newValue;
      this.updateSelectedTimeFromValue();
    },
    time: {
      handler(newValue) {
        this.$emit("input", newValue);
      },
    },
  },
  mounted() {
    this.updateSelectedTimeFromValue();
    // 외부 클릭 감지를 위한 이벤트 리스너 추가
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeDestroy() {
    // 컴포넌트 파괴 시 이벤트 리스너 제거
    document.removeEventListener("click", this.handleOutsideClick);
  },
  methods: {
    updateSelectedTimeFromValue() {
      if (this.time) {
        const timeParts = this.time.split(":");
        if (timeParts.length === 3) {
          this.selectedHour = parseInt(timeParts[0], 10);
          this.selectedMinute = parseInt(timeParts[1], 10);
          this.selectedSecond = parseInt(timeParts[2], 10);
        }
      }
    },
    onTimeInput(type, value) {
      switch (type) {
        case "h":
          this.tempHour = value;
          break;
        case "m":
          this.tempMinute = value;
          break;
        case "s":
          this.tempSecond = value;
          break;
      }
      this.applySelection();
    },
    toggleTimePicker(event) {
      if (this.readonly) return;

      // 이벤트 버블링 방지
      event.stopPropagation();

      if (!this.showTimePicker) {
        this.showTimePicker = true;
        this.tempHour = this.selectedHour;
        this.tempMinute = this.selectedMinute;
        this.tempSecond = this.selectedSecond;

        // 스크롤 위치 조정을 위한 setTimeout
        this.$nextTick(() => {
          this.scrollToSelectedTimes();
        });
      } else {
        this.showTimePicker = false;
      }
    },
    handleOutsideClick(event) {
      const container = this.$el;
      if (this.showTimePicker && container && !container.contains(event.target)) {
        this.showTimePicker = false;
      }
    },
    scrollToSelectedTimes() {
      const scrollers = this.$el.querySelectorAll(".time-scroller");
      const activeElements = this.$el.querySelectorAll(".time-item.active");

      if (scrollers.length === 3 && activeElements.length === 3) {
        for (let i = 0; i < 3; i++) {
          if (activeElements[i]) {
            const containerHeight = scrollers[i].clientHeight;
            const itemHeight = activeElements[i].clientHeight;
            const scrollTop = activeElements[i].offsetTop - containerHeight / 2 + itemHeight / 2;
            scrollers[i].scrollTop = scrollTop;
          }
        }
      }
    },
    selectHour(hour) {
      this.tempHour = hour;
    },
    selectMinute(minute) {
      this.tempMinute = minute;
    },
    selectSecond(second) {
      this.tempSecond = second;
    },
    padZero(num) {
      return num.toString().padStart(2, "0");
    },
    cancelSelection() {
      this.showTimePicker = false;
    },
    applySelection() {
      this.selectedHour = this.tempHour;
      this.selectedMinute = this.tempMinute;
      this.selectedSecond = this.tempSecond;

      if (this.showSeconds) {
        this.time = `${this.padZero(this.selectedHour)}:${this.padZero(this.selectedMinute)}:${this.padZero(this.selectedSecond)}`;
      } else {
        this.time = `${this.padZero(this.selectedHour)}:${this.padZero(this.selectedMinute)}:00`;
      }

      this.showTimePicker = false;
      this.$emit("change", this.time);
    },
    clearTime() {
      this.time = "";
      this.selectedHour = 0;
      this.selectedMinute = 0;
      this.selectedSecond = 0;
      this.$emit("change", this.time);
    },
  },
};
</script>

<style scoped>
.n-input::v-deeep .number-input {
  padding: 0;
  border: 0;
  text-align: center !important;
}
.time-selector-container {
  position: relative;
  width: 100%;
}

.time-input-wrapper {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
}

.time-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 6px 10px;
  border-radius: 5px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  background-color: white;
  text-align: left;
  cursor: pointer;
}

.time-display:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.placeholder {
  color: #999;
}

.clock-icon {
  font-size: 24px;
  margin-left: 6px;
  border-left: 1px solid #cdcdcd;
  padding-left: 6px;
}

.clear-btn {
  position: absolute;
  right: 50px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  height: 100%;
  z-index: 2;
}

.clear-icon {
  font-size: 16px;
  color: #999;
}

.clear-btn:hover .clear-icon {
  color: #666;
}

.time-picker-panel {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  width: 300px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
}

.time-picker-header {
  padding: 15px;
  text-align: center;
  background-color: #f8f9fa;
  border-bottom: 1px solid #eee;
  border-radius: 8px 8px 0 0;
}

.preview-time {
  font-size: 24px;
  font-weight: bold;
  color: #333;
}

.time-picker-content {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 15px;
}

.time-column {
  width: 60px;
  text-align: center;
}

.column-label {
  margin-bottom: 8px;
  font-size: 14px;
  color: #666;
}

.time-scroller {
  height: 180px;
  overflow-y: auto;
  scrollbar-width: thin;
  position: relative;
  border-radius: 4px;
  background-color: #f8f9fa;
}

.time-scroller::-webkit-scrollbar {
  width: 4px;
}

.time-scroller::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 4px;
}

.time-item {
  padding: 8px 0;
  cursor: pointer;
  transition: all 0.2s;
}

.time-item:hover {
  background-color: #e9ecef;
}

.time-item.active {
  background-color: rgba(var(--vs-secondary), 1);
  color: white;
  font-weight: bold;
}

.time-item.selected {
  background-color: #e2f0ff;
  color: rgba(var(--vs-secondary), 1);
  font-weight: bold;
  border-left: 3px solid rgba(var(--vs-secondary), 1);
}

.time-divider {
  margin: 0 5px;
  font-size: 20px;
  font-weight: bold;
  color: #666;
  align-self: center;
}

.time-picker-footer {
  display: flex;
  justify-content: flex-end;
  padding: 10px 15px;
  border-top: 1px solid #eee;
}

.cancel-btn,
.apply-btn {
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 10px;
  border: none;
}

.cancel-btn {
  background-color: #f8f9fa;
  color: #666;
}

.apply-btn {
  background-color: rgba(var(--vs-secondary), 1);
  color: white;
}

.cancel-btn:hover {
  background-color: #e9ecef;
}

.apply-btn:hover {
  background-color: rgba(var(--vs-secondary), 1);
}
</style>
